import type { Collab } from "@/types/collab"

/**
 * OKS-385 (TRD KD-3) — client-side THIS WEEK / LATER grouping for the
 * creator missions home (W8).
 *
 * The backend (`CreatorMyMissionsQuery`) emits a FLAT collab list and its
 * prop shape is contract-locked; it does not pre-group. We derive the two
 * buckets here from data already on the wire — `mission.urgency`
 * (server-computed: `default` / `due_today` / `overdue`) and
 * `mission.deadline_at` (a calendar-Date ISO string `YYYY-MM-DD`, nullable).
 *
 * ## Rule
 *
 * A collab is **THIS WEEK** when it is already due/overdue OR its deadline
 * falls on/before the end of the current calendar week:
 *   - `urgency === "due_today" | "overdue"` → THIS WEEK (already due), OR
 *   - `deadline_at` is set AND on/before the upcoming Sunday → THIS WEEK.
 * Everything else — a later deadline, or no deadline at all — is **LATER**.
 *
 * Ordering within each bucket is STABLE (input order preserved) so the
 * backend's own sort survives the partition.
 *
 * All comparisons are calendar-day based (date-only, the local civil date)
 * to match the date-only `deadline_at` wire shape and the server's
 * day-anchored `urgency` derivation — no hour math, no timezone drift.
 */

export type CollabGroupKey = "overdue" | "this_week" | "later"

export interface GroupedCollabs {
  /**
   * OKS-472 — overdue collabs (`urgency === "overdue"`), surfaced as their
   * own section ABOVE THIS WEEK on the §05 Crew Home (UX Spec §3 dense
   * edge). Empty `[]` → the section header is hidden.
   */
  overdue: Collab[]
  thisWeek: Collab[]
  later: Collab[]
}

/**
 * Civil (date-only) value for a `Date`, dropping the time component so two
 * instants on the same calendar day compare equal. Days since epoch.
 */
function civilDayNumber(date: Date): number {
  return Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000,
  )
}

/**
 * Parse a `YYYY-MM-DD` deadline string into a civil day number, or `null`
 * when the value is missing/malformed (treated as "no deadline" → LATER).
 */
function parseDeadlineDayNumber(deadlineAt: string | null): number | null {
  if (!deadlineAt) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(deadlineAt)
  if (!match) return null
  const [, y, m, d] = match
  return Math.floor(Date.UTC(Number(y), Number(m) - 1, Number(d)) / 86_400_000)
}

/**
 * Civil day number of the upcoming Sunday (end of the current week) for
 * `now`. Sunday is treated as the last day of the week; if `now` is already
 * Sunday, the same day is the boundary. `Date#getDay()` → 0 (Sun)…6 (Sat).
 */
function endOfWeekDayNumber(now: Date): number {
  const today = civilDayNumber(now)
  const daysUntilSunday = (7 - now.getDay()) % 7
  return today + daysUntilSunday
}

/**
 * Bucket a single collab. Exported for unit-level boundary testing.
 *
 * @param now Injectable clock for deterministic tests (defaults to `new Date()`).
 */
export function collabGroupKey(
  collab: Collab,
  now: Date = new Date(),
): CollabGroupKey {
  const { urgency, deadline_at: deadlineAt } = collab.mission

  // OKS-472 — overdue is its own bucket (was folded into THIS WEEK pre-§05).
  if (urgency === "overdue") {
    return "overdue"
  }

  if (urgency === "due_today") {
    return "this_week"
  }

  const deadlineDay = parseDeadlineDayNumber(deadlineAt)
  if (deadlineDay === null) {
    return "later"
  }

  return deadlineDay <= endOfWeekDayNumber(now) ? "this_week" : "later"
}

/**
 * Partition a flat collab list into THIS WEEK / LATER, preserving the input
 * order within each bucket. An empty bucket is returned as `[]` so callers
 * can hide the corresponding section header (UX Spec §3).
 *
 * @param now Injectable clock for deterministic tests (defaults to `new Date()`).
 */
export function groupCollabs(
  collabs: Collab[],
  now: Date = new Date(),
): GroupedCollabs {
  const overdue: Collab[] = []
  const thisWeek: Collab[] = []
  const later: Collab[] = []

  for (const collab of collabs) {
    switch (collabGroupKey(collab, now)) {
      case "overdue":
        overdue.push(collab)
        break
      case "this_week":
        thisWeek.push(collab)
        break
      default:
        later.push(collab)
    }
  }

  return { overdue, thisWeek, later }
}
